import {
  ActionReducerMapBuilder,
  createAction,
  createSlice,
  CreateSliceOptions,
  Slice,
  SliceCaseReducers,
} from '@reduxjs/toolkit';
import { NoInfer } from '@reduxjs/toolkit/dist/tsHelpers';

/** @warning you cannot add this action your slice, this must be done ONLY through createResettableSlice */
export const resetAction = createAction('ResettableSlices/reset');

const addResetActionToBuilder = <State>(
  getInitialState: () => State,
  builder: ActionReducerMapBuilder<NoInfer<State>>,
) => {
  builder.addCase(resetAction, () => getInitialState());
};

const addResetActionToConfig = <
  State,
  CaseReducers extends SliceCaseReducers<State>,
  Name extends string = string,
>(
  config: CreateSliceOptions<State, CaseReducers, Name>,
) => {
  const extra = config.extraReducers;
  // a factory initialState is re-evaluated on every reset, so a slice whose
  // fresh state depends on something outside redux (e.g. branding cached in
  // localStorage) resets to what is true *now*, not to a boot-time snapshot
  const getInitialState = (
    typeof config.initialState === 'function' ? config.initialState : () => config.initialState
  ) as () => State;

  if (typeof extra === 'object') {
    extra[resetAction().type] = () => getInitialState();
  } else if (typeof extra === 'function') {
    config.extraReducers = (builder: ActionReducerMapBuilder<NoInfer<State>>) => {
      extra(builder);
      addResetActionToBuilder(getInitialState, builder);
    };
  } else {
    config.extraReducers = (builder: ActionReducerMapBuilder<NoInfer<State>>) => {
      addResetActionToBuilder(getInitialState, builder);
    };
  }
};

/**
 * adds resetAction as an extraReducer to the slice
 * resetAction will reset the reducer to its initialState
 */
export function createResettableSlice<
  State,
  CaseReducers extends SliceCaseReducers<State>,
  Name extends string = string,
>(options: CreateSliceOptions<State, CaseReducers, Name>): Slice<State, CaseReducers, Name> {
  addResetActionToConfig(options);
  return createSlice(options);
}
